import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    ActivatedRoute,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Router,
    Event
} from '@angular/router';
import {
    distinctUntilChanged, filter, map, Subscription
} from 'rxjs';
// In general, we should NOT be importing services from other apps, but these will eventually come from a shared package
import { PageTitleService } from '../../../../src/app/services/page-title.service';
import { PageLoadingService } from '../../../../src/app/services/page-loading.service';
import { ToastMessageStateService } from '../../../../src/app/core/toast-message/services/toast-message.state.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    title = 'public';
    private routerSubscription: Subscription;
    private documentTitleSubscription: Subscription;

    constructor(
        private router: Router,
        private pageTitleService: PageTitleService,
        public pageLoadingService: PageLoadingService,
        private toastMessageStateService: ToastMessageStateService
    ) {
        this.routerSubscription = this.router.events
            .pipe(untilDestroyed(this))
            .subscribe((event: Event) => {
                this.navigationInterceptor(event);
            });
    }

    ngOnInit(): void {
        this.setDocumentTitle();
    }

    // Shows and hides the loading spinner during RouterEvent changes
    navigationInterceptor(event: Event): void {
        if (event instanceof NavigationStart) {
            this.pageLoadingService.start();
        }
        if (event instanceof NavigationEnd) {
            setTimeout(() => this.pageLoadingService.stop(), 0);
            this.toastMessageStateService.displayMessageOnNavigate();
        }

        // Set loading state to false in both of the below events to hide the spinner in case a request fails
        if (event instanceof NavigationCancel) {
            setTimeout(() => this.pageLoadingService.stop(), 0);
            this.toastMessageStateService.displayMessageOnNavigate();
        }
        if (event instanceof NavigationError) {
            setTimeout(() => this.pageLoadingService.stop(), 0);
            this.toastMessageStateService.displayMessageOnNavigate();
        }
    }

    // Manage document title
    // https://dev.to/brandontroberts/setting-page-titles-natively-with-the-angular-router-393j
    setDocumentTitle(): void {
        this.documentTitleSubscription = this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                distinctUntilChanged(),
                map(() => {
                    let count = 0;
                    let route: ActivatedRoute = this.router.routerState.root;
                    let routeData = {};
                    while (route?.firstChild) {
                        if (count > 300) break;
                        route = route.firstChild;
                        count += 1;
                    }
                    if (route.snapshot.data) {
                        routeData = route?.snapshot.data;
                    }
                    return routeData;
                }),
                untilDestroyed(this)
            )
            .subscribe((routeData: any) => {
                if (!routeData.isAngularJsRoute) {
                    // A null title is OK here, the service will provide a default title for null
                    this.pageTitleService.setTitle(routeData.title);
                }
            });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
        if (this.documentTitleSubscription) {
            this.documentTitleSubscription.unsubscribe();
        }
    }
}
